import React, { useCallback, useRef, useState } from 'react';
import { Input, Spin } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { CategoryItem, ItemsPickerSectionProps } from './types';
import {
  CheckIconWrap,
  DraggableTagItem,
  DropdownEmptyMsg,
  DropdownLoadingWrap,
  SearchWrapper,
  SelectedBoxEmpty,
  SelectedBoxLabel,
  SelectedItemsBox,
  SelectedItemText,
  SelectedTagClose,
  SuggestionItem,
  SuggestionsDropdown,
  TagGripHandle,
  TagsWrap,
} from './styles';

// Preserves the exact same react-dnd mechanics the Categories tab has always
// used for reordering selected items (drag over another pill to swap it into
// that position) — only the visual presentation changed.
const DND_ITEM_TYPE = 'categorySelectedItem';

interface DraggablePillProps {
  item: CategoryItem;
  index: number;
  moveItem: (fromIndex: number, toIndex: number) => void;
  onRemove: (id: number | string) => void;
}

const DraggablePill: React.FC<DraggablePillProps> = ({ item, index, moveItem, onRemove }) => {
  const [{ isDragging }, dragRef] = useDrag({
    type: DND_ITEM_TYPE,
    item: { index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [, dropRef] = useDrop({
    accept: DND_ITEM_TYPE,
    hover: (dragged: { index: number }) => {
      if (dragged.index !== index) {
        moveItem(dragged.index, index);
        dragged.index = index;
      }
    },
  });

  return (
    <DraggableTagItem ref={(node) => dragRef(dropRef(node))} $isDragging={isDragging}>
      <TagGripHandle>
        <FontAwesomeIcon icon={faGripVertical} />
      </TagGripHandle>
      {item.item}
      <SelectedTagClose onClick={() => onRemove(item.id)}>✕</SelectedTagClose>
    </DraggableTagItem>
  );
};

const ItemsPickerSection: React.FC<ItemsPickerSectionProps> = ({
  allItems,
  selected,
  onChange,
  isDarkMode,
  isLoading,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [show, setShow] = useState(false);

  const filtered = query.trim()
    ? allItems.filter((i) => i.item.toLowerCase().includes(query.toLowerCase()))
    : allItems;

  const handleToggle = (item: CategoryItem) => {
    if (selected.some((s) => s.id === item.id)) {
      onChange(selected.filter((s) => s.id !== item.id));
    } else {
      onChange([...selected, item]);
    }
  };

  const handleSelectAll = () => {
    if (selected.length === allItems.length) {
      onChange([]);
    } else {
      onChange(allItems);
    }
  };

  const handleRemove = useCallback(
    (id: number | string) => {
      onChange(selected.filter((s) => s.id !== id));
    },
    [selected, onChange]
  );

  const moveItem = useCallback(
    (fromIndex: number, toIndex: number) => {
      const updated = [...selected];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      onChange(updated);
    },
    [selected, onChange]
  );

  return (
    <>
      <SearchWrapper ref={wrapperRef}>
        <Input
          placeholder="Search items..."
          value={query}
          allowClear
          size="large"
          suffix={isLoading ? <Spin size="small" /> : undefined}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setShow(true)}
          onBlur={() => setTimeout(() => setShow(false), 150)}
        />
        {show && (
          <SuggestionsDropdown $isDark={isDarkMode}>
            {isLoading ? (
              <DropdownLoadingWrap>
                <Spin size="small" />
              </DropdownLoadingWrap>
            ) : filtered.length === 0 ? (
              <DropdownEmptyMsg>
                {query.trim() ? 'No results found.' : 'No items available.'}
              </DropdownEmptyMsg>
            ) : (
              <>
                <SuggestionItem
                  $isDark={isDarkMode}
                  $isSelectAll
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectAll();
                  }}
                >
                  {selected.length === allItems.length ? 'Unselect All' : 'Select All'}
                </SuggestionItem>
                {filtered.map((item) => {
                  const isChecked = selected.some((s) => s.id === item.id);
                  return (
                    <SuggestionItem
                      key={item.id}
                      $isDark={isDarkMode}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleToggle(item);
                      }}
                    >
                      {isChecked ? (
                        <SelectedItemText>{item.item}</SelectedItemText>
                      ) : (
                        <span>{item.item}</span>
                      )}
                      {isChecked && (
                        <CheckIconWrap>
                          <CheckOutlined />
                        </CheckIconWrap>
                      )}
                    </SuggestionItem>
                  );
                })}
              </>
            )}
          </SuggestionsDropdown>
        )}
      </SearchWrapper>

      <SelectedItemsBox $isDark={isDarkMode}>
        <SelectedBoxLabel $isDark={isDarkMode}>Selected Items (drag to reorder):</SelectedBoxLabel>
        {selected.length === 0 ? (
          <SelectedBoxEmpty $isDark={isDarkMode}>No items selected.</SelectedBoxEmpty>
        ) : (
          <DndProvider backend={HTML5Backend}>
            <TagsWrap>
              {selected.map((item, index) => (
                <DraggablePill
                  key={item.id}
                  item={item}
                  index={index}
                  moveItem={moveItem}
                  onRemove={handleRemove}
                />
              ))}
            </TagsWrap>
          </DndProvider>
        )}
      </SelectedItemsBox>
    </>
  );
};

export default ItemsPickerSection;
